/**
 * Payment tokenization plumbing (DNKADM-0223 U1).
 *
 * This module consumes only public, non-secret client config. The Square
 * application id and Square location id come from
 * `GET /v2/public/{tenantSlug}/payments/client-config`; no secret is read from
 * env or git. The public read deliberately uses plain `fetch` instead of the
 * shared bearer client so it sends no bearer token, no CSRF header, and no
 * cookies.
 */
import { env } from '@/config/env';
import { minorUnitFactor } from '@/utils/v2Format';

export type PaymentProvider = 'square' | 'stripe' | 'tilopay' | (string & {});
export type PaymentEnvironment = 'production' | 'sandbox';

export interface PaymentClientConfig {
  configured: boolean;
  provider: PaymentProvider | null;
  environment: PaymentEnvironment | null;
  clientConfig: Record<string, string>;
}

const DEGRADED_PAYMENT_CONFIG: PaymentClientConfig = {
  configured: false,
  provider: null,
  environment: null,
  clientConfig: {},
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 ? text : null;
}

function parseClientConfig(raw: unknown): Record<string, string> {
  if (!isObject(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    const text = asText(value);
    if (text !== null) out[key] = text;
  }
  return out;
}

function parsePaymentConfig(raw: unknown): PaymentClientConfig {
  const body = isObject(raw) ? raw : {};
  const environment = asText(body['environment']);
  return {
    configured: body['configured'] === true,
    provider: asText(body['provider']) as PaymentProvider | null,
    environment:
      environment === 'sandbox' || environment === 'production' ? environment : null,
    clientConfig: parseClientConfig(body['clientConfig']),
  };
}

export async function getPaymentClientConfig(
  tenantSlug: string,
  locationRef: string,
): Promise<PaymentClientConfig> {
  const tenant = tenantSlug.trim();
  const location = locationRef.trim();
  if (tenant.length === 0 || location.length === 0) return DEGRADED_PAYMENT_CONFIG;

  const params = new URLSearchParams({ locationRef: location });
  const url = `${env.journixApiBaseUrl}/v2/public/${encodeURIComponent(
    tenant,
  )}/payments/client-config?${params.toString()}`;
  const res = await fetch(url, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) return DEGRADED_PAYMENT_CONFIG;
  if (!res.ok) {
    throw new Error(`Payment client-config fetch failed: ${res.status}`);
  }

  const data = await res.json().catch(() => ({}));
  return parsePaymentConfig(data);
}

export class CardTokenizationError extends Error {
  readonly detail: string;
  readonly validation: boolean;

  constructor(detail = '', opts: { validation?: boolean } = {}) {
    super('CARD_TOKENIZATION_FAILED');
    this.name = 'CardTokenizationError';
    this.detail = detail;
    this.validation = opts.validation === true;
  }
}

export interface SquareTokenError {
  message?: string;
  detail?: string;
  field?: string;
  code?: string;
  category?: string;
}

export interface SquareTokenResult {
  status: string;
  token?: string;
  errors?: SquareTokenError[];
}

export interface SquareCard {
  attach: (element: HTMLElement | string) => Promise<void>;
  tokenize: () => Promise<SquareTokenResult>;
  destroy?: () => Promise<boolean | void>;
}

export interface SquarePayments {
  card: () => Promise<SquareCard>;
}

export interface SquareSdk {
  payments: (applicationId: string, locationId: string) => SquarePayments;
}

const SQUARE_SDK_SRC: Record<PaymentEnvironment, string> = {
  production: 'https://web.squarecdn.com/v1/square.js',
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
};

export function loadSquareSdk(
  environment: PaymentEnvironment | null,
): Promise<SquareSdk> {
  const existing = (window as unknown as { Square?: SquareSdk }).Square;
  if (existing) return Promise.resolve(existing);

  const src = SQUARE_SDK_SRC[environment === 'sandbox' ? 'sandbox' : 'production'];

  return new Promise<SquareSdk>((resolve, reject) => {
    const finish = () => {
      const sdk = (window as unknown as { Square?: SquareSdk }).Square;
      if (sdk) resolve(sdk);
      else reject(new Error('Square SDK loaded but window.Square is undefined'));
    };

    const prior = document.querySelector<HTMLScriptElement>('script[data-square-sdk]');
    if (prior) {
      prior.addEventListener('load', finish, { once: true });
      prior.addEventListener(
        'error',
        () => reject(new Error('Square SDK failed to load')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-square-sdk', '');
    script.addEventListener('load', finish, { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error('Square SDK failed to load')),
      { once: true },
    );
    document.head.appendChild(script);
  });
}

export function minorToString(amountMinor: number, currencyCode: string): string {
  const factor = minorUnitFactor(currencyCode);
  return (amountMinor / factor).toFixed(Math.log10(factor));
}

export function toMinorUnits(amount: string | number, currencyCode: string): number {
  const factor = minorUnitFactor(currencyCode);
  const numeric = typeof amount === 'number' ? amount : Number(amount.trim());
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * factor);
}

export function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const rand = () => Math.random().toString(36).slice(2, 12);
  return `idmp-${Date.now().toString(36)}-${rand()}-${rand()}`;
}
